// How long a club holds a player, as in MLB:
// - Minor league free agency: after MINORS_FREE_AGENCY_SEASONS professional seasons, a minor leaguer on a minor league
//   deal who isn't one of his organization's prospects leaves on his own at the end of the season. Nobody waits in the
//   farm system forever.
// - Team control: a player who came up in this league stays with his club until he has CONTROL_SEASONS seasons in the
//   majors (or, if he never debuted, CONTROL_SEASONS professional seasons). His expiring contract is renewed for one
//   year: the minimum for the first 3 seasons, then arbitration at 40%, 60% and 80% of what he would ask on the market.
//   Players who debuted before the league started keep their real contracts, which already cover their control years.
//   So do imported veterans whose stats only start with the league: a player who turned pro more than CONTROL_SEASONS
//   seasons before the league started can't be in his control years at his debut.
// - Minor league limit: each organization keeps at most maxMinorsSize players in the minors.
import { PLAYER } from "../../../common/constants.ts";
import type { Player } from "../../../common/types.ts";
import { idb } from "../../db/index.ts";
import { g, helpers, logEvent } from "../../util/index.ts";
import { minorsProspects, organizationPlayers } from "../trade/organizationPlayers.ts";
import addToFreeAgents from "./addToFreeAgents.ts";
import release from "./release.ts";
import { getNumPlayersTradedAwayNormalizedAll } from "./getNumPlayersTradedAwayNormalized.ts";

export const MINORS_FREE_AGENCY_SEASONS = 6;
export const CONTROL_SEASONS = 6;
const ARBITRATION_SHARES = [0.4, 0.6, 0.8];

// Full seasons since he turned pro (drafted or signed)
export const proSeasons = (p: Pick<Player, "draft">, season: number) =>
	p.draft.year > 0 ? Math.max(0, season - p.draft.year) : 0;

// Major league seasons in which he played
const mlbSeasons = (p: Pick<Player, "stats">) =>
	[...new Set(p.stats.filter((row) => !row.playoffs && row.gp > 0).map((row) => row.season))].sort((a, b) => a - b);

// A minor league deal: at most 1.5x the minimum. Bigger contracts are big league money the club keeps paying.
export const minorLeagueDeal = (p: Pick<Player, "contract">) => p.contract.amount <= 1.5 * g.get("minContract");

/** Seasons of team control already used, or undefined if he can leave (control over, or a real pre-league contract). */
export const controlSeasonsUsed = (p: Pick<Player, "stats" | "draft">, season: number) => {
	const seasons = mlbSeasons(p);
	if (seasons.length === 0) {
		const pro = proSeasons(p, season);
		return pro < CONTROL_SEASONS ? 0 : undefined;
	}
	if (seasons[0]! < g.get("startingSeason") || p.draft.year < g.get("startingSeason") - CONTROL_SEASONS) {
		return undefined;
	}
	return seasons.length < CONTROL_SEASONS ? seasons.length : undefined;
};

/** One-year salary under team control: the minimum, then arbitration as a share of his market demand. */
export const controlSalary = (used: number, marketAmount: number) => {
	const share = ARBITRATION_SHARES[used - 3];
	if (share === undefined) {
		return g.get("minContract");
	}
	return Math.max(g.get("minContract"), helpers.roundContract(marketAmount * share));
};

/** Leaves the minors as a free agent, owed nothing (minor league deal). */
export const releaseFromMinors = async (p: Player, reason: string) => {
	const tid = p.minorTid ?? p.tid;
	logEvent({
		type: "release",
		text: `<a href="${helpers.leagueUrl(["player", p.pid])}">${p.firstName} ${p.lastName}</a> left the <a href="${helpers.leagueUrl([
			"minor_league",
			`${g.get("teamInfoCache")[tid]?.abbrev}_${tid}`,
		])}">${g.get("teamInfoCache")[tid]?.name}</a> minor league system: ${reason}.`,
		showNotification: false,
		pids: [p.pid],
		tids: [tid],
	});
	p.tid = tid;
	delete p.minorTid;
	addToFreeAgents(p, await getNumPlayersTradedAwayNormalizedAll());
	await idb.cache.players.put(p);
};

/** End of season: veterans of the farm system on minor league deals who aren't prospects become free agents. */
export const minorLeagueFreeAgency = async () => {
	const season = g.get("season");
	const released: number[] = [];
	for (const t of await idb.cache.teams.getAll()) {
		if (t.disabled) continue;
		const org = await organizationPlayers(t.tid);
		const prospects = minorsProspects(org);
		for (const p of org) {
			if (
				p.tid === PLAYER.MINORS &&
				!prospects.has(p.pid) &&
				minorLeagueDeal(p) &&
				proSeasons(p, season) >= MINORS_FREE_AGENCY_SEASONS
			) {
				await releaseFromMinors(p, `minor league free agent after ${proSeasons(p, season)} seasons as a pro`);
				released.push(p.pid);
			}
		}
	}
	return released;
};

/** How many minor leaguers an organization has above the limit (0 if none or no limit). */
export const numMinorsOverLimit = (org: Player[]) => {
	const max = g.get("maxMinorsSize") ?? 0;
	const minors = org.filter((p) => p.tid === PLAYER.MINORS).length;
	return max > 0 ? Math.max(0, minors - max) : 0;
};

/** Minor leaguers an AI organization lets go to get down to the limit: minor league deals first (they cost nothing to
 * release), least valuable first, never one of its prospects. */
export const minorsOverLimit = (org: Player[]) => {
	const over = numMinorsOverLimit(org);
	if (over === 0) {
		return [];
	}
	const prospects = minorsProspects(org);
	return org
		.filter((p) => p.tid === PLAYER.MINORS && !prospects.has(p.pid))
		.sort((a, b) => Number(!minorLeagueDeal(a)) - Number(!minorLeagueDeal(b)) || a.value - b.value || a.pid - b.pid)
		.slice(0, over);
};

/** Released from the minors: free on a minor league deal; otherwise his club still owes the contract. */
export const releaseMinorLeaguer = async (p: Player, reason: string) => {
	if (minorLeagueDeal(p)) {
		await releaseFromMinors(p, reason);
		return;
	}
	if (p.minorTid !== undefined) {
		p.tid = p.minorTid;
		delete p.minorTid;
	}
	await release(p, false);
};
